import { Grid, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { TraceHistory } from '../../types';
import { useAlarms } from '../alarms/api/getAlarms';
import { getTracesAroundLogItem } from '../../api/snapshotAlarmLogApi';
import { parseTraceFromBuffer } from '../../utils/traceUtils';
import { differenceInSeconds, format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { grey } from '@mui/material/colors';
import { findClosestTraceIndex } from '../alarmLog/utils/logManipulation';
import CenteredCircularProgress from '../../components/CenteredCircularProgress';
import SpectrumChart from 'features/spectrumChart/SpectrumChart';

interface Props {
  logItemId: number;
  analyzerId: number;
  snapshotId: number;
  alarmId: number;
  timestamp: string;
  alternativeClickHandler?: () => void;
}
const AlarmHistoryTraceDisplay = ({
  logItemId,
  analyzerId,
  snapshotId,
  alarmId,
  timestamp,
  alternativeClickHandler,
}: Props) => {
  const navigate = useNavigate();

  const { data: alarms } = useAlarms({});
  const thisAlarm = alarms?.find((alarm) => alarm.id === alarmId);
  const [traces, setTraces] = useState<TraceHistory[] | null>(null);
  const [parsedTrace, setParsedTrace] = useState<number[]>([]);
  useEffect(() => {
    const getTrace = async () => {
      const newTraces = await getTracesAroundLogItem(logItemId, 1, 1);
      if (newTraces) {
        setTraces(newTraces);
        setParsedTrace(parseTraceFromBuffer(newTraces[0]?.trace));
      }
    };
    getTrace();
  }, [logItemId]);

  const clickHandler = alternativeClickHandler
    ? alternativeClickHandler
    : async () => {
        const traces = await getTracesAroundLogItem(logItemId, 50, 50);
        const fromDate = traces[0].time;
        const toDate = traces[traces.length - 1].time;
        const centerIndex = findClosestTraceIndex(traces, timestamp);
        navigate(
          `/trace-history?analyzer=${traces[0].analyzer_id}&snapshot=${traces[0].snapshot_id}&start=${fromDate}&end=${toDate}&trace=${centerIndex}`
        );
      };

  const loading = !traces;

  const isThereACloseEnoughTrace =
    traces &&
    traces.length > 0 &&
    Math.abs(
      differenceInSeconds(new Date(traces[0].time), new Date(timestamp))
    ) < 31;

  if (loading)
    return (
      <Grid
        item
        container
        xs={3}
        component={Paper}
        style={{ position: 'relative' }}
        direction='column'
      >
        <Grid
          item
          container
          justifyContent='center'
          alignItems='center'
          sx={{ p: '.5rem', pt: '19%', pb: '19%' }}
        >
          <CenteredCircularProgress />
        </Grid>
      </Grid>
    );

  if (!isThereACloseEnoughTrace)
    return (
      <Grid
        item
        container
        xs={3}
        component={Paper}
        style={{ position: 'relative' }}
        direction='column'
      >
        <Grid item container justifyContent='center'>
          <Typography variant='body2'>
            {thisAlarm?.name} at {format(new Date(timestamp), 'Pp')}
          </Typography>
        </Grid>
        <Grid
          item
          container
          justifyContent='center'
          alignItems='center'
          sx={{ p: '.5rem', pt: '19%', pb: '19%' }}
        >
          <Typography color={grey[500]} variant='body2'>
            No history recorded.
          </Typography>
        </Grid>
      </Grid>
    );

  return (
    <Grid
      item
      xs={3}
      component={Paper}
      style={{ position: 'relative' }}
      onClick={() => clickHandler()}
    >
      <Grid item container justifyContent='center'>
        <Typography variant='body2'>
          {thisAlarm?.name} at {format(new Date(timestamp), 'Pp')}
        </Typography>
      </Grid>

      <SpectrumChart
        analyzerId={analyzerId}
        snapshotId={snapshotId}
        staticSnapshot={traces[0]}
        staticTrace={parsedTrace}
      />
    </Grid>
  );
};

export default AlarmHistoryTraceDisplay;
